import React from 'react';
import PropTypes from 'prop-types';
import filter from 'lodash/filter';
import map from 'lodash/map';
import sortBy from 'lodash/sortBy';
import isUndefined from 'lodash/isUndefined';
import find from 'lodash/find';
import UserDatabases from '@Services/UserDatabases';
import Catalyst from '@Services/Catalyst';
import AccordionHeader from '@Components/Results/common/accordion-header.component';
import { REACTION_ADDITIONAL_INFO_VIEW_MODES } from '@Constants/ElementConstants';
import ReactReactionAdditionalInfoHelper from '@Components/Results/services/react-reaction-additional-info-helper.service';
import ReactTableColumnSwitcherHelper from '@Components/Results/services/react-table-columns-switcher-helper.service';
import ReactionAdditionalDataContentsComponent from '@Components/Results/reactions/reaction-details/additional-data/reaction-additional-data-contents.component';
import TableColumnsSwitcherComponent from '@Components/Results/reactions/reaction-details/table-columns-switcher/table-columns-switcher.component';

export default class ReactionAdditionalDataComponent extends React.Component {
  constructor(props) {
    super(props);
    const { viewMode, stages, roles, data } = this.props;

    const allHeaders = { ...REACTION_ADDITIONAL_INFO_VIEW_MODES[viewMode] };
    const shownHeaders = filter(allHeaders, (header) => {
      const matchStages = !header.stages || stages;
      const matchRoles = !header.roles || roles;
      return matchStages && matchRoles;
    });

    let rows = map(data, (dataItem) => ({
      id: dataItem.id,
      cells: this.mapReactantInfo(dataItem, shownHeaders),
    }));

    rows = this.markContentExists(rows, shownHeaders);
    rows = this.markLeadingTrailingStages(rows);

    this.state = {
      formatedData: {
        layout: 'list',
        headers: shownHeaders,
        rows,
      },
      isOpen: false,
    };
    this.containerRef = React.createRef();
    this.columnSwitcherRef = React.createRef();
    this.toggleAccordion = this.toggleAccordion.bind(this);
    this.mapReactantInfo = this.mapReactantInfo.bind(this);
    this.showAllNonEmptyCols = this.showAllNonEmptyCols.bind(this);
    this.updateHeaders = this.updateHeaders.bind(this);
  }

  toggleAccordion() {
    const { isOpen } = this.state;
    if (!isOpen) {
      const { accordionTitle: title, reactionId, databaseId } = this.props;
      const dbName = UserDatabases.getDbName(databaseId);

      Catalyst.trackContentView('contentView', {
        id: `contentview:${reactionId}`,
        type: `${Catalyst.getPrefix()}:contentview:${dbName}:${title}`,
      });
    }

    this.setState({ isOpen: !isOpen });
  }

  // eslint-disable-next-line class-methods-use-this
  mapReactantInfo(reactant, headers) {
    return headers.map((header) => {
      const value = reactant[header.id] ? reactant[header.id] : '';
      const appendix =
        header.appendix && value && (!header.appendixIgnoreIf || !value.match(header.appendixIgnoreIf)) ? ` ${header.appendix}` : '';

      return {
        order: header.order,
        id: header.id,
        value: value + appendix,
      };
    });
  }

  // eslint-disable-next-line class-methods-use-this
  markContentExists(rows, headers) {
    rows.forEach((row) => {
      // eslint-disable-next-line no-param-reassign
      row.cells = sortBy(row.cells, (cell) => cell.order);

      row.cells.forEach((cell) => {
        if (!isUndefined(cell.value) && cell.value.length) {
          // Find matching header and set hasContent to true
          const headerItem = find(headers, { id: cell.id });
          headerItem.hasContent = true;
        }
      });
    });

    return rows;
  }

  // eslint-disable-next-line class-methods-use-this
  markLeadingTrailingStages(rows) {
    const lastIndex = rows.length - 1;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < rows.length; i++) {
      if (
        i < lastIndex &&
        ReactReactionAdditionalInfoHelper.getStage(rows[i]) === ReactReactionAdditionalInfoHelper.getStage(rows[i + 1])
      ) {
        // eslint-disable-next-line no-param-reassign
        rows[i].leading = true;
      }
      if (i > 0 && ReactReactionAdditionalInfoHelper.getStage(rows[i]) === ReactReactionAdditionalInfoHelper.getStage(rows[i - 1])) {
        // eslint-disable-next-line no-param-reassign
        rows[i].trailing = true;
      }
    }

    return rows;
  }

  updateHeaders(headers) {
    const { formatedData } = this.state;
    formatedData.headers = headers;
    this.setState({ formatedData });
  }

  showAllNonEmptyCols() {
    this.columnSwitcherRef.current.showAllNonEmptyCols();
  }

  render() {
    const { isOpen, formatedData } = this.state;
    const { uniqueId, accordionTitle, databaseId } = this.props;

    return (
      <div className={`rx-accordion-item ${isOpen ? '' : 'closed'}`}>
        <AccordionHeader isOpen={isOpen} accordionId={uniqueId} toggle={this.toggleAccordion}>
          {accordionTitle}
        </AccordionHeader>

        {isOpen && (
          <div id={uniqueId} className="rx-accordion-content" aria-expanded={isOpen} ref={this.containerRef}>
            <div className="rx-accordion-content additional-data-tables">
              <div className="switcher-wrapper">
                <TableColumnsSwitcherComponent
                  headers={formatedData.headers}
                  uniqueId={uniqueId}
                  containerRef={this.containerRef}
                  ref={this.columnSwitcherRef}
                  updateHeaders={this.updateHeaders}
                />
              </div>
              <style>{ReactTableColumnSwitcherHelper.generateCssRules(uniqueId, formatedData.headers)}</style>
              <ReactionAdditionalDataContentsComponent
                databaseId={databaseId}
                uniqueId={uniqueId}
                formatedData={formatedData}
                showAllNonEmptyCols={this.showAllNonEmptyCols}
              />
            </div>
          </div>
        )}
      </div>
    );
  }
}

ReactionAdditionalDataComponent.propTypes = {
  data: PropTypes.array.isRequired,
  viewMode: PropTypes.string.isRequired,
  accordionTitle: PropTypes.string.isRequired,
  uniqueId: PropTypes.string.isRequired,
  databaseId: PropTypes.string.isRequired,
  reactionId: PropTypes.string,
  stages: PropTypes.string,
  roles: PropTypes.string,
};
